import {
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Args,
  type RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Options,
  useRentalManagerUserPropertyMultiplexV2WebApiHandler_update,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type {
  ApiResponseErrorDto,
  MultiplexUpdateFieldErrorV2WebDto,
  UserPropertyMultiplexUpdateResponseV2WebDto,
  UserPropertyMultiplexUpdateV2RequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';

/**
 * Amenity-related fields on {@link UserPropertyMultiplexUpdateV2RequestDto} used when mapping multi-edit payloads
 * into multiplex `postData`.
 */
export const MULTIPLEX_AMENITY_KEYS = [
  'balconyDeckPatioPorch',
  'hasAttachedGarageParking',
  'hasBicycleStorage',
  'hasDetachedGarageParking',
  'hasDisabledAccess',
  'hasEvCharger',
  'hasSwimmingPool',
  'offStreetParking',
  'isRentersInsuranceRequired',
] as const satisfies ReadonlyArray<keyof UserPropertyMultiplexUpdateV2RequestDto>;

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

const collectMultiplexUpdateFieldErrors = (
  responseData: UserPropertyMultiplexUpdateResponseV2WebDto | undefined,
): Array<MultiplexUpdateFieldErrorV2WebDto> => {
  if (!responseData?.units?.length) {
    return [];
  }

  return responseData.units.flatMap((unit) => unit.errors ?? []);
};

/**
 * Runtime guard beyond compile-time args:
 * `postData` must be a plain object (not an array) with a non-empty `multiplexBuildingSessionId` string.
 */
const assertMultiplexUpdateMutationArgs = (
  args: RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Args,
): void => {
  const { postData } = args;

  if (typeof postData !== 'object' || postData === null || Array.isArray(postData)) {
    throw new TypeError(
      'useMultiplexUpdate: `postData` must be a non-array object (UserPropertyMultiplexUpdateV2RequestDto).',
    );
  }

  const sessionId = postData.multiplexBuildingSessionId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexUpdate: `postData.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }
};

export type UseMultiplexUpdateOptions = Omit<
  RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexUpdateMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexV2WebApiHandler_update>;

export type UseMultiplexUpdateResult = Omit<
  BaseMultiplexUpdateMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Args,
    options?: Parameters<BaseMultiplexUpdateMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexUpdateMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexV2WebApiHandler_update_Args,
    options?: Parameters<BaseMultiplexUpdateMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexUpdateMutation['mutateAsync']>;
  data: BaseMultiplexUpdateMutation['data'];
  error: BaseMultiplexUpdateMutation['error'];
  isPending: BaseMultiplexUpdateMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  fieldErrors: Array<MultiplexUpdateFieldErrorV2WebDto>;
  success: boolean | undefined;
  units: UserPropertyMultiplexUpdateResponseV2WebDto['units'] | undefined;
};

/**
 * Multiplex listing update: wraps the generated `update` mutation (POST `/api/web/v2/users/properties/multiplex/update`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), `fieldErrors` (flattened unit-level errors from the response
 * body), and `success` / `units` from the last successful `data`.
 *
 * **Calling the mutation:** Use `{ postData: { multiplexBuildingSessionId, ...fields } }` matching
 * `UserPropertyMultiplexUpdateV2RequestDto` (optional listing fields). Compile-time typing applies to `mutate` /
 * `mutateAsync`; runtime checks enforce non-array `postData` and a non-empty session id string.
 */
const useMultiplexUpdate = (mutationOptions: UseMultiplexUpdateOptions = {}): UseMultiplexUpdateResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexV2WebApiHandler_update({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'useRentalManagerUserPropertyMultiplexV2WebApiHandler_update',
        details: 'useMultiplexUpdate',
        ...metaOverrides?.context,
      },
    },
  });

  /*
   * Many mutation wrappers leave data/error/isPending on `...rest` only. We destructure `data` here to derive
   * fieldErrors/success/units, and list error/isPending next to `errors` for a clear return shape. Callers still get the
   * same fields they would from spreading the underlying mutation result.
   */
  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexUpdateMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexUpdateMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    fieldErrors: collectMultiplexUpdateFieldErrors(data),
    success: data?.success,
    units: data?.units,
  };
};

export default useMultiplexUpdate;
